/**
 * Icon caching utility for dynamic Lucide icon loading
 * Improves performance by caching icon imports
 */

import * as LucideIcons from 'lucide-react';

const iconCache = new Map<string, any>();

/**
 * Convert kebab-case to PascalCase for Lucide icon names
 */
function toPascalCase(str: string): string {
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

/**
 * Get a Lucide icon component with caching
 * @param iconName - Icon name in kebab-case (e.g., 'target', 'zap-icon')
 * @param fallback - Fallback icon component (default: Target)
 */
export function getCachedIcon(
  iconName: string, 
  fallback: any = LucideIcons.Target
): any {
  // Check cache first
  if (iconCache.has(iconName)) {
    return iconCache.get(iconName);
  }

  // Get icon from Lucide
  const pascalName = toPascalCase(iconName);
  const icon = (LucideIcons as any)[pascalName] || fallback;
  
  // Cache the result
  iconCache.set(iconName, icon);
  
  return icon;
}

/**
 * Preload commonly used icons
 */
export function preloadCommonIcons() {
  const commonIcons = [
    'target', 'zap', 'activity', 'gift', 'trophy', 'shield',
    'phone', 'users', 'calendar', 'file-text', 'check-circle',
    'star', 'heart', 'trending-up', 'briefcase'
  ];

  commonIcons.forEach(iconName => {
    getCachedIcon(iconName);
  });
}

/**
 * Clear the icon cache (useful for testing)
 */
export function clearIconCache() {
  iconCache.clear();
}
